// ============================================================
// ChannelPermissions — MEN-259 [V1-06b] slice 2 : éditeur ACL
// par salon (overwrites rôle / membre, tri-state allow/inherit/
// deny) + preview des permissions EFFECTIVES.
//
// Acceptance #2 : un overwrite persiste (PUT/DELETE
// /admin/channels/:chid/permissions) et invalide le cache
// effective-perms (bumpPermVersion côté serveur) ; synchro temps
// réel via `channel:permissions-updated`.
//
// Acceptance #3 : bloc « Effective perms » 100% calculé serveur
// (GET /admin/channels/:chid/permissions/effective?userId=X →
// computeEffectivePermissions MEN-231). Aucun calcul de permission
// côté client : l'UI ne fait que composer les bitmasks allow/deny.
// ============================================================

import { useEffect, useMemo, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { CHANNEL_PERMISSION_FLAGS, PERMISSION_INFO, Permission } from '@siverix/shared';
import type {
  AdminChannelOverride,
  AdminCommunityChannel,
  AdminMember,
  AdminRole,
} from '@siverix/api-client';
import { ApiError } from '@siverix/api-client';
import { api } from '../api';
import { ErrorBox } from './OverviewPage';
import { useSocket } from '../hooks/useSocket';

// [2.12] Permissions qui ont un effet dans ce type de salon, en français
// (packages/shared/src/roles.ts). Les autres ne sont pas proposées.
function channelEntries(
  type: string | undefined,
): Array<{ flag: string; label: string; bit: bigint }> {
  const flags = CHANNEL_PERMISSION_FLAGS[type === 'VOICE' ? 'VOICE' : 'TEXT'];
  return flags.map((flag) => ({
    flag,
    label: PERMISSION_INFO[flag].channelLabel ?? PERMISSION_INFO[flag].label,
    bit: Permission[flag],
  }));
}

const lbl: React.CSSProperties = {
  display: 'block',
  fontSize: '12px',
  color: 'var(--color-text-muted)',
  marginBottom: 4,
};
const inp: React.CSSProperties = {
  padding: '8px 10px',
  borderRadius: 6,
  border: '1px solid var(--color-border)',
  background: 'var(--color-bg)',
  color: 'var(--color-text)',
  fontSize: '13px',
};
const btn: React.CSSProperties = {
  padding: '8px 14px',
  borderRadius: 6,
  border: '1px solid var(--color-accent)',
  background: 'var(--color-accent)',
  color: '#fff',
  fontSize: '13px',
  cursor: 'pointer',
};
const btnGhost: React.CSSProperties = {
  padding: '6px 12px',
  borderRadius: 6,
  border: '1px solid var(--color-border)',
  background: 'transparent',
  color: 'var(--color-text)',
  fontSize: '12px',
  cursor: 'pointer',
};
const btnDanger: React.CSSProperties = {
  ...btnGhost,
  borderColor: 'var(--color-danger, #c0392b)',
  color: 'var(--color-danger, #c0392b)',
};

type TriState = 'allow' | 'inherit' | 'deny';

function toBig(s: string): bigint {
  try {
    return BigInt(s);
  } catch {
    return 0n;
  }
}
function triFor(allow: bigint, deny: bigint, bit: bigint): TriState {
  if ((allow & bit) !== 0n) return 'allow';
  if ((deny & bit) !== 0n) return 'deny';
  return 'inherit';
}
interface Draft {
  allow: bigint;
  deny: bigint;
}

export function ChannelPermissions(): JSX.Element {
  const { id: communityId } = useParams<{ id: string }>();
  const [channels, setChannels] = useState<AdminCommunityChannel[]>([]);
  const [roles, setRoles] = useState<AdminRole[]>([]);
  const [members, setMembers] = useState<AdminMember[]>([]);
  const [channelId, setChannelId] = useState<string>('');
  const [overrides, setOverrides] = useState<AdminChannelOverride[]>([]);
  const [tab, setTab] = useState<'roles' | 'users'>('roles');
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [drafts, setDrafts] = useState<Record<string, Draft>>({});
  const [savingId, setSavingId] = useState<string | null>(null);
  const [addId, setAddId] = useState<string>('');
  const [effUser, setEffUser] = useState<string>('');
  const [effPerms, setEffPerms] = useState<bigint | null>(null);
  const [effLoading, setEffLoading] = useState(false);
  const { socket } = useSocket();

  async function fetchMeta(): Promise<void> {
    if (!communityId) return;
    try {
      const [chs, rs, ms] = await Promise.all([
        api.adminListChannels(communityId),
        api.adminListRoles(communityId),
        api.adminListMembers(communityId),
      ]);
      setChannels(chs);
      setRoles(rs);
      setMembers(ms);
      setChannelId((cur) => cur || chs[0]?.id || '');
      setErr(null);
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setLoading(false);
    }
  }

  async function fetchOverrides(chid: string): Promise<void> {
    try {
      const ow = await api.adminListChannelPerms(chid);
      setOverrides(ow);
      setDrafts(
        Object.fromEntries(ow.map((o) => [o.id, { allow: toBig(o.allow), deny: toBig(o.deny) }])),
      );
      setErr(null);
    } catch (e) {
      setErr((e as Error).message);
    }
  }

  useEffect(() => {
    void fetchMeta();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [communityId]);

  useEffect(() => {
    if (channelId) {
      void fetchOverrides(channelId);
      setEffPerms(null);
    }
  }, [channelId]);

  useEffect(() => {
    if (!socket || !channelId) return;
    const refresh = (): void => void fetchOverrides(channelId);
    const refreshMeta = (): void => void fetchMeta();
    socket.on('channel:permissions-updated', refresh);
    socket.on('role:created', refreshMeta);
    socket.on('role:updated', refreshMeta);
    socket.on('role:deleted', refreshMeta);
    socket.on('member:roles-updated', refreshMeta);
    return () => {
      socket.off('channel:permissions-updated', refresh);
      socket.off('role:created', refreshMeta);
      socket.off('role:updated', refreshMeta);
      socket.off('role:deleted', refreshMeta);
      socket.off('member:roles-updated', refreshMeta);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [socket, channelId]);

  const memberLabel = (id: string): string => {
    const m = members.find((x) => x.id === id);
    return m?.user?.displayName ?? m?.user?.username ?? id.slice(0, 8);
  };
  const roleLabel = (id: string): string => roles.find((r) => r.id === id)?.name ?? id.slice(0, 8);

  const shown = useMemo(
    () => overrides.filter((o) => (tab === 'roles' ? o.roleId : o.memberId)),
    [overrides, tab],
  );
  const entries = useMemo(
    () => channelEntries(channels.find((c) => c.id === channelId)?.type),
    [channels, channelId],
  );

  const addable = useMemo(() => {
    if (tab === 'roles') {
      const used = new Set(overrides.filter((o) => o.roleId).map((o) => o.roleId));
      return roles
        .filter((r) => !used.has(r.id))
        .sort((a, b) => b.position - a.position || a.name.localeCompare(b.name))
        .map((r) => ({ id: r.id, label: r.name }));
    }
    const used = new Set(overrides.filter((o) => o.memberId).map((o) => o.memberId));
    return members
      .filter((m) => !used.has(m.id))
      .map((m) => ({
        id: m.id,
        label: m.user?.displayName ?? m.user?.username ?? m.id.slice(0, 8),
      }));
  }, [tab, overrides, roles, members]);

  function setTri(id: string, bit: bigint, next: TriState): void {
    setDrafts((d) => {
      const cur = d[id] ?? { allow: 0n, deny: 0n };
      let { allow, deny } = cur;
      allow &= ~bit;
      deny &= ~bit;
      if (next === 'allow') allow |= bit;
      else if (next === 'deny') deny |= bit;
      return { ...d, [id]: { allow, deny } };
    });
  }

  async function saveOverride(o: AdminChannelOverride): Promise<void> {
    const d = drafts[o.id];
    if (!d) return;
    setSavingId(o.id);
    try {
      await api.adminPutChannelPerm(o.channelId, {
        roleId: o.roleId ?? undefined,
        memberId: o.memberId ?? undefined,
        allow: d.allow.toString(),
        deny: d.deny.toString(),
      });
      await fetchOverrides(o.channelId);
      setErr(null);
    } catch (e) {
      const msg =
        e instanceof ApiError ? `Échec (${e.status}) : ${e.message}` : (e as Error).message;
      setErr(msg);
    } finally {
      setSavingId(null);
    }
  }

  async function deleteOverride(o: AdminChannelOverride): Promise<void> {
    if (!window.confirm('Supprimer cet overwrite ?')) return;
    try {
      await api.adminDeleteChannelPerm(o.channelId, o.id);
      await fetchOverrides(o.channelId);
    } catch (e) {
      setErr((e as Error).message);
    }
  }

  async function addOverride(): Promise<void> {
    if (!channelId || !addId) return;
    try {
      await api.adminPutChannelPerm(channelId, {
        roleId: tab === 'roles' ? addId : undefined,
        memberId: tab === 'users' ? addId : undefined,
        allow: '0',
        deny: '0',
      });
      setAddId('');
      await fetchOverrides(channelId);
    } catch (e) {
      setErr((e as Error).message);
    }
  }

  async function computeEffective(): Promise<void> {
    if (!channelId || !effUser) return;
    setEffLoading(true);
    try {
      const r = await api.adminEffectivePerms(channelId, effUser);
      setEffPerms(toBig(r.permissions));
      setErr(null);
    } catch (e) {
      setEffPerms(null);
      const msg =
        e instanceof ApiError ? `Échec (${e.status}) : ${e.message}` : (e as Error).message;
      setErr(msg);
    } finally {
      setEffLoading(false);
    }
  }

  const triBtn = (active: boolean, tone: 'allow' | 'deny' | 'inherit'): React.CSSProperties => ({
    width: 26,
    height: 22,
    fontSize: 11,
    borderRadius: 4,
    cursor: 'pointer',
    border: '1px solid var(--color-border)',
    color: active ? '#fff' : 'var(--color-text-muted)',
    background: !active
      ? 'transparent'
      : tone === 'allow'
        ? 'var(--color-success, #2e7d32)'
        : tone === 'deny'
          ? 'var(--color-danger, #c0392b)'
          : 'var(--color-border)',
  });

  return (
    <div>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 12, flexWrap: 'wrap' }}>
        <h1 style={{ marginTop: 0 }}>ACL des salons</h1>
        <Link to="/communities" style={{ fontSize: 13, color: 'var(--color-accent)' }}>
          ← Communautés
        </Link>
        {communityId && (
          <>
            <Link
              to={`/communities/${communityId}/roles`}
              style={{ fontSize: 13, color: 'var(--color-accent)' }}
            >
              Rôles & permissions
            </Link>
            <Link
              to={`/communities/${communityId}/members`}
              style={{ fontSize: 13, color: 'var(--color-accent)' }}
            >
              Membres & rôles
            </Link>
          </>
        )}
      </div>
      <p style={{ color: 'var(--color-text-muted)', fontSize: '14px' }}>
        Overwrites par salon (tri-state : <strong>A</strong>=autoriser, <strong>–</strong>=hériter,{' '}
        <strong>D</strong>=refuser). Les permissions effectives sont calculées 100% côté serveur.
      </p>

      {err && <ErrorBox message={err} />}

      {loading ? (
        <div>Chargement…</div>
      ) : channels.length === 0 ? (
        <div style={{ color: 'var(--color-text-muted)' }}>Aucun salon dans cette communauté.</div>
      ) : (
        <>
          <div style={{ display: 'flex', gap: 12, alignItems: 'flex-end', flexWrap: 'wrap' }}>
            <div>
              <label style={lbl}>Salon</label>
              <select style={inp} value={channelId} onChange={(e) => setChannelId(e.target.value)}>
                {channels.map((c) => (
                  <option key={c.id} value={c.id}>
                    #{c.name} ({c.type})
                  </option>
                ))}
              </select>
            </div>
            <div style={{ display: 'flex', gap: 6 }}>
              <button
                type="button"
                style={tab === 'roles' ? btn : btnGhost}
                onClick={() => setTab('roles')}
              >
                Rôles
              </button>
              <button
                type="button"
                style={tab === 'users' ? btn : btnGhost}
                onClick={() => setTab('users')}
              >
                Membres
              </button>
            </div>
          </div>

          <div style={{ margin: '14px 0', display: 'flex', gap: 8, alignItems: 'center' }}>
            <select style={inp} value={addId} onChange={(e) => setAddId(e.target.value)}>
              <option value="">
                + Ajouter un overwrite {tab === 'roles' ? 'de rôle' : 'de membre'}…
              </option>
              {addable.map((a) => (
                <option key={a.id} value={a.id}>
                  {a.label}
                </option>
              ))}
            </select>
            <button
              type="button"
              style={{ ...btn, opacity: addId ? 1 : 0.5 }}
              disabled={!addId}
              onClick={() => void addOverride()}
            >
              Ajouter
            </button>
          </div>

          {shown.length === 0 ? (
            <div style={{ color: 'var(--color-text-muted)', fontSize: 13 }}>
              Aucun overwrite {tab === 'roles' ? 'de rôle' : 'de membre'} sur ce salon.
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              {shown.map((o) => {
                const d = drafts[o.id] ?? { allow: 0n, deny: 0n };
                const owner = o.roleId ? roleLabel(o.roleId) : memberLabel(o.memberId ?? '');
                return (
                  <div
                    key={o.id}
                    style={{
                      background: 'var(--color-surface)',
                      border: '1px solid var(--color-border)',
                      borderRadius: 10,
                      padding: 14,
                    }}
                  >
                    <div
                      style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        marginBottom: 10,
                      }}
                    >
                      <strong>
                        {o.roleId ? '🎭 ' : '👤 '}
                        {owner}
                      </strong>
                      <div style={{ display: 'flex', gap: 8 }}>
                        <button
                          type="button"
                          style={{ ...btn, opacity: savingId === o.id ? 0.6 : 1 }}
                          disabled={savingId === o.id}
                          onClick={() => void saveOverride(o)}
                        >
                          {savingId === o.id ? 'Enregistrement…' : 'Enregistrer'}
                        </button>
                        <button
                          type="button"
                          style={btnDanger}
                          onClick={() => void deleteOverride(o)}
                        >
                          Suppr.
                        </button>
                      </div>
                    </div>
                    <div
                      style={{
                        display: 'grid',
                        gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))',
                        gap: '4px 16px',
                      }}
                    >
                      {entries.map(({ flag, label, bit }) => {
                        const t = triFor(d.allow, d.deny, bit);
                        return (
                          <div
                            key={flag}
                            style={{
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'space-between',
                              fontSize: 12,
                              gap: 8,
                            }}
                          >
                            <span
                              style={{
                                overflow: 'hidden',
                                textOverflow: 'ellipsis',
                                whiteSpace: 'nowrap',
                              }}
                            >
                              {label}
                            </span>
                            <span style={{ display: 'flex', gap: 3 }}>
                              <button
                                type="button"
                                title="Autoriser"
                                style={triBtn(t === 'allow', 'allow')}
                                onClick={() => setTri(o.id, bit, 'allow')}
                              >
                                A
                              </button>
                              <button
                                type="button"
                                title="Neutre"
                                style={triBtn(t === 'inherit', 'inherit')}
                                onClick={() => setTri(o.id, bit, 'inherit')}
                              >
                                –
                              </button>
                              <button
                                type="button"
                                title="Refuser"
                                style={triBtn(t === 'deny', 'deny')}
                                onClick={() => setTri(o.id, bit, 'deny')}
                              >
                                D
                              </button>
                            </span>
                          </div>
                        );
                      })}
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Acceptance #3 : preview effective perms (100% serveur) */}
          <div
            style={{
              marginTop: 22,
              background: 'var(--color-surface)',
              border: '1px solid var(--color-border)',
              borderRadius: 10,
              padding: 16,
            }}
          >
            <h3 style={{ marginTop: 0 }}>Droits effectifs d’un membre dans ce salon</h3>
            <div style={{ display: 'flex', gap: 8, alignItems: 'flex-end', flexWrap: 'wrap' }}>
              <div>
                <label style={lbl}>Membre</label>
                <select style={inp} value={effUser} onChange={(e) => setEffUser(e.target.value)}>
                  <option value="">— Choisir —</option>
                  {members.map((m) => (
                    <option key={m.id} value={m.userId}>
                      {memberLabel(m.id)}
                    </option>
                  ))}
                </select>
              </div>
              <button
                type="button"
                style={{ ...btn, opacity: effUser ? 1 : 0.5 }}
                disabled={!effUser || effLoading}
                onClick={() => void computeEffective()}
              >
                {effLoading ? 'Calcul…' : 'Calculer'}
              </button>
            </div>
            {effPerms !== null && (
              <div
                style={{
                  marginTop: 12,
                  display: 'grid',
                  gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))',
                  gap: '2px 16px',
                  fontSize: 12,
                }}
              >
                {entries.map(({ flag, label, bit }) => {
                  const on = (effPerms & bit) !== 0n;
                  return (
                    <span
                      key={flag}
                      style={{
                        color: on ? 'var(--color-success, #2e7d32)' : 'var(--color-text-muted)',
                      }}
                    >
                      {on ? '✓' : '✕'} {label}
                    </span>
                  );
                })}
              </div>
            )}
          </div>
        </>
      )}
    </div>
  );
}
